"use client";
import { useEffect, useRef } from "react";
import { useEditor } from "novel";
import { useStorage } from "@/lib/storage/provider";

export default function AssetResolver() {
  const { editor } = useEditor();
  const { adapter } = useStorage();
  const cacheRef = useRef<Map<string, string>>(new Map()); // blobKey -> objectURL

  useEffect(() => {
    if (!editor) return;
    let disposed = false;

    const settle = async () => {
      try {
        const dom = editor.view.dom as HTMLElement;
        const imgs = Array.from(dom.querySelectorAll<HTMLImageElement>('img[src^="ploom://asset/"]'));
        for (const img of imgs) {
          const src = img.getAttribute("src") || "";
          const key = src.replace("ploom://asset/", "");
          if (!key) continue;
          if (!cacheRef.current.has(key)) {
            const blob = await adapter.getAsset(key);
            if (!blob) continue;
            const url = URL.createObjectURL(blob);
            cacheRef.current.set(key, url);
          }
          const objectUrl = cacheRef.current.get(key)!;
          // preserve original
          if (!img.getAttribute("data-asset-src")) img.setAttribute("data-asset-src", src);
          if (img.src !== objectUrl) img.src = objectUrl;
        }
      } catch {}
    };

    // Run initially and after each transaction
    // Small debounce via animation frame
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => void settle());
    };
    schedule();
    const onTr = () => schedule();
    editor.on?.("transaction", onTr);
    return () => {
      editor.off?.("transaction", onTr);
      cancelAnimationFrame(raf);
      disposed = true;
      // cleanup object URLs
      for (const url of cacheRef.current.values()) URL.revokeObjectURL(url);
      cacheRef.current.clear();
    };
  }, [editor, adapter]);

  return null;
}

